import React from 'react';

import {
  ActionIcon,
  Select,
  TextInput,
  Title,
  Tooltip,
  Button,
  useMantineTheme,
  Space,
  createStyles,
} from '@mantine/core';
import { IconX, IconFilterOff, IconPlus, IconSearch } from '@tabler/icons-react';

import { AthleteDocumentSearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xs,
    display: 'grid',
    gridTemplateColumns: 'auto auto auto auto min-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `'title search type status clear waiver contract'`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'auto auto auto min-content min-content min-content',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: `
        'title title title waiver contract contract'
        'search type status status status clear'
      `,
    },
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'auto auto min-content min-content min-content',
      gridTemplateRows: 'auto auto auto',
      gridTemplateAreas: `
        'title title waiver contract contract'
        'search search search search search'
        'type status status status status'
        '. . . . clear'
      `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto auto min-content min-content min-content',
      gridTemplateRows: 'auto auto auto',
      gridTemplateAreas: `
        'title title waiver contract contract'
        'search search search search search'
        'type type type type type'
        'status status status status status'
        '. . . . clear'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  search: {
    gridArea: 'search',
  },
  type: {
    gridArea: 'type',
  },
  status: {
    gridArea: 'status',
  },
  clear: {
    gridArea: 'clear',
  },
  waiver: {
    gridArea: 'waiver',
  },
  contract: {
    gridArea: 'contract',
  },
}));

const AthleteDocumentsFilters = ({
  searchFilters,
  initialFilterValues,
  setSearchFilters,
  openCreateWaiver,
  openCreateContract,
  permission = true,
}: {
  searchFilters: AthleteDocumentSearchFilters;
  initialFilterValues: AthleteDocumentSearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<AthleteDocumentSearchFilters>>;
  openCreateWaiver: () => void;
  openCreateContract: () => void;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const handleTypeChange = (value: AthleteDocumentSearchFilters['type']): void => {
    setSearchFilters(prev => ({ ...prev, type: value }));
  };
  const handleStatusChange = (value: AthleteDocumentSearchFilters['isSigned']): void => {
    setSearchFilters(prev => ({ ...prev, isSigned: value }));
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchFilters(prev => ({ ...prev, search: e?.currentTarget?.value }));
  };
  const handleClearFilters = (): void => {
    setSearchFilters(initialFilterValues);
  };
  const handleClearSearchFilter = (): void => {
    setSearchFilters(prev => ({ ...prev, search: '' }));
  };

  return (
    <div className={classes.header}>
      <Title order={2} className={classes.title}>
        Documents
      </Title>
      <TextInput
        className={classes.search}
        placeholder='Search by description'
        icon={<IconSearch size={theme.fontSizes.md} />}
        onChange={handleSearchChange}
        value={searchFilters.search}
        rightSection={
          !!searchFilters.search ? (
            <ActionIcon onClick={handleClearSearchFilter} size='md'>
              <IconX size={theme.fontSizes.md} />
            </ActionIcon>
          ) : (
            <Space />
          )
        }
      />
      <Select
        className={classes.type}
        placeholder='Select type'
        data={[
          { label: 'Contract', value: 'contract' },
          { label: 'Waiver', value: 'waiver' },
        ]}
        value={searchFilters.type}
        onChange={handleTypeChange}
        clearable
      />
      <Select
        className={classes.status}
        placeholder='Select status'
        data={[
          { label: 'Signed', value: 'True' },
          { label: 'Not Signed', value: 'False' },
        ]}
        value={searchFilters.isSigned}
        onChange={handleStatusChange}
        clearable
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>
      <Button
        className={classes.waiver}
        onClick={openCreateWaiver}
        leftIcon={<IconPlus size={theme.fontSizes.md} />}
        size='xs'
        disabled={!permission}
      >
        Waiver
      </Button>
      <Button
        className={classes.contract}
        onClick={openCreateContract}
        leftIcon={<IconPlus size={theme.fontSizes.md} />}
        size='xs'
        disabled={!permission}
      >
        Contract
      </Button>
    </div>
  );
};

export default AthleteDocumentsFilters;
